/**
 * Quick Actions Component
 * Icon-driven action cards for common tasks
 */

import React from 'react';
import { Box, Typography, Grid, styled } from '@mui/material';
import { Link } from 'react-router-dom';
import { Card } from '../ui/Card';
import { colors } from '../../theme/colors';
import DevicesIcon from '@mui/icons-material/Devices';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CategoryIcon from '@mui/icons-material/Category';
import PeopleIcon from '@mui/icons-material/People';

interface QuickAction {
  label: string;
  icon: React.ReactNode;
  path: string;
  color: string;
}

const ActionCard = styled(Card)({
  cursor: 'pointer',
  height: '100%',
  transition: 'all 0.25s ease-in-out',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '4px',
    height: '100%',
    background: colors.primary.main,
    transform: 'scaleY(0)',
    transformOrigin: 'bottom',
    transition: 'transform 0.25s ease-in-out',
  },
  '&:hover': {
    transform: 'translateX(4px)',
    '&::before': {
      transform: 'scaleY(1)',
    },
    '& .action-icon': {
      transform: 'scale(1.1)',
    },
  },
});

export const QuickActions: React.FC = () => {
  const actions: QuickAction[] = [
    {
      label: 'Все устройства',
      icon: <DevicesIcon />,
      path: '/devices-list',
      color: colors.primary.main,
    },
    {
      label: 'Добавить устройство',
      icon: <AddCircleOutlineIcon />,
      path: '/devices-list',
      color: colors.success.main,
    },
    {
      label: 'Семейства устройств',
      icon: <CategoryIcon />,
      path: '/device-families',
      color: colors.secondary.main,
    },
    {
      label: 'Контракторы',
      icon: <PeopleIcon />,
      path: '/contractors',
      color: colors.warning.main,
    },
  ];

  return (
    <Grid container spacing={3}>
      {actions.map((action, index) => (
        <Grid item xs={12} sm={6} lg={3} key={index}>
          <Link to={action.path} style={{ textDecoration: 'none' }}>
            <ActionCard variant="default" hover>
              <Box
                sx={{
                  p: 3,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Box
                  className="action-icon"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    backgroundColor: `${action.color}15`,
                    flexShrink: 0,
                    transition: 'transform 0.3s ease-in-out',
                    '& svg': {
                      fontSize: '1.75rem',
                      color: action.color,
                    },
                  }}
                >
                  {action.icon}
                </Box>
                <Typography
                  sx={{
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: colors.text.primary,
                  }}
                >
                  {action.label}
                </Typography>
              </Box>
            </ActionCard>
          </Link>
        </Grid>
      ))}
    </Grid>
  );
};
